import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { IBalanceFlow, IBalanceStepDBData } from '../sp-types/balanceFlowType';
import { BalanceSuggestedInstructions } from '../sp-types/buyingInstructionsType';
import { StatusType } from '../sp-types/statusType';
import { updateItem } from './genericFB';

const BALANCE_FLOW_COLLECTION_NAME = 'balance-flow';
const BALANCE_SUGGESTED_INSTRUCTIONS_COLLECTION = 'balance-instructions';
const DB_LOCATION = db;

export async function createBalanceFlow(
  data: IBalanceFlow,
  balanceId: string,
  callback?: (_success: boolean, _createdObject?: IBalanceFlow) => void,
) {
  try {
    const docRef = doc(DB_LOCATION, BALANCE_FLOW_COLLECTION_NAME, balanceId);
    await setDoc(docRef, data);
    const docSnap = await getDoc(docRef);
    const balanceFlow = docSnap.data() as IBalanceFlow;
    balanceFlow.id = docSnap.id;
    balanceFlow.updatedAt = Timestamp.now();
    return balanceFlow;
  } catch (err) {
    console.log(`Error creating balance flow: ${err}`);
    callback && callback(false, undefined);
  }
}

export async function deleteBalanceFlow(balanceFlowId: string, callback?: (success: boolean) => void) {
  try {
    const docRef = doc(DB_LOCATION, BALANCE_FLOW_COLLECTION_NAME, balanceFlowId);
    await deleteDoc(docRef);
    callback && callback(true);
  } catch (err) {
    console.error(`Failed to delete Balance With given ID ${balanceFlowId}:`, err);
    callback && callback(false);
  }
}

export const deleteBalancesOfAccount = async (accountId: string) => {
  const balancesRef = collection(DB_LOCATION, BALANCE_FLOW_COLLECTION_NAME);
  const q = query(balancesRef, where('accountId', '==', accountId));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    console.log('No matching documents.');
    return;
  }
  for (const aDoc of querySnapshot.docs) {
    await deleteDoc(aDoc.ref);
  }

  console.log(`Deleted ${querySnapshot.size} documents where accountId == ${accountId}`);
};

export async function getOpenBalanceFlow(
  accountId: string,
  formulaId: string,
  statuses: string[] = ['underwriting'],
): Promise<IBalanceFlow | null> {
  const balanceFlowRef = collection(DB_LOCATION, BALANCE_FLOW_COLLECTION_NAME);
  try {
    const q = query(
      balanceFlowRef,
      where('accountId', '==', accountId),
      where('status', 'in', statuses),
      where('formulaId', '==', formulaId),
      orderBy('balanceDate', 'desc'),
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const balanceFlows: IBalanceFlow[] = querySnapshot.docs.map((item) => item.data() as IBalanceFlow);
      return balanceFlows[0];
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting documents:', error);
    return null;
  }
}

export async function getBalanceFlowById(balanceId: string): Promise<IBalanceFlow | null> {
  try {
    const docRef = doc(DB_LOCATION, BALANCE_FLOW_COLLECTION_NAME, balanceId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const balanceFlow = docSnap.data() as IBalanceFlow;
      balanceFlow.id = docSnap.id;
      return balanceFlow;
    } else {
      console.log(`No balance flow found with ID: ${balanceId}`);
      return null;
    }
  } catch (err) {
    console.error(`Error getting balance flow with ID '${balanceId}':`, err);
    return null;
  }
}

export async function getBalanceFlowsByStatusesAndAccount(
  accountId: string,
  statuses: StatusType[],
): Promise<IBalanceFlow[]> {
  try {
    const balanceFlowRef = collection(DB_LOCATION, BALANCE_FLOW_COLLECTION_NAME);
    const q = query(balanceFlowRef, where('accountId', '==', accountId), where('status', 'in', statuses));
    const querySnapshot = await getDocs(q);
    const balanceFlows: IBalanceFlow[] = querySnapshot.docs.map((doc) => {
      const balanceFlow = doc.data() as IBalanceFlow;
      balanceFlow.id = doc.id;
      return balanceFlow;
    });

    return balanceFlows;
  } catch (error) {
    console.error(
      `Error getting balance flows by statuses [${statuses.join(', ')}] and accountId '${accountId}':`,
      error,
    );
    return [];
  }
}

export async function updateBalanceFlowProperty(
  balanceFlowId: string,
  propertyName: keyof IBalanceFlow,
  propertyValue: any,
) {
  try {
    const docRef = doc(DB_LOCATION, BALANCE_FLOW_COLLECTION_NAME, balanceFlowId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const updatedData = { ...docSnap.data(), [propertyName]: propertyValue, updatedAt: Timestamp.now() };
      await updateDoc(docRef, updatedData);
      console.log(`Balance property '${propertyName}' updated successfully.`);
    } else {
      console.log(`Balance with ID '${balanceFlowId}' does not exist.`);
    }
  } catch (err) {
    console.error(`Error updating Balance property: ${err}`);
  }
}

export async function updateBalanceFlow(
  balanceFlowId: string,
  newBalanceFlow: Partial<IBalanceFlow>,
): Promise<IBalanceFlow | null> {
  return updateItem<IBalanceFlow>(balanceFlowId, newBalanceFlow, BALANCE_FLOW_COLLECTION_NAME);
}

export async function updateBalanceStepStatusFB(
  balanceFlowId: string,
  currentSteps: IBalanceStepDBData[],
  stepName: IBalanceStepDBData['stepName'],
  status: IBalanceStepDBData['status'],
) {
  const updatedBalanceSteps = currentSteps.map((step) => {
    if (step.stepName === stepName) {
      return { ...step, status };
    }
    return step;
  });
  updateBalanceFlowProperty(balanceFlowId, 'steps', updatedBalanceSteps);
}

export async function checkIfOpenBalanceFlow(accountId: string, formulaId: string): Promise<boolean> {
  const balancesRef = collection(DB_LOCATION, BALANCE_FLOW_COLLECTION_NAME);
  try {
    const q = query(
      balancesRef,
      where('accountId', '==', accountId),
      where('status', 'in', ['underwriting', 'started']),
      where('formulaId', '==', formulaId),
    );
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error getting documents:', error);
    return false;
  }
}

///////----------
// Functions for handling balanceInstructions
//////-----------

export async function addBalanceInstruction(
  data: BalanceSuggestedInstructions,
  id: string,
  callback?: (success: boolean, createdObject?: BalanceSuggestedInstructions) => void,
) {
  try {
    const docRef = doc(DB_LOCATION, BALANCE_SUGGESTED_INSTRUCTIONS_COLLECTION, id);
    await setDoc(docRef, data);
    const docSnap = await getDoc(docRef);
    const balanceInstruction = docSnap.data() as BalanceSuggestedInstructions;
    return balanceInstruction;
  } catch (err) {
    console.log(`Error creating balance instruction: ${err}`);
    callback && callback(false);
  }
}

export async function deleteBalanceInstruction(id: string, callback?: (success: boolean) => void) {
  try {
    const docRef = doc(DB_LOCATION, BALANCE_SUGGESTED_INSTRUCTIONS_COLLECTION, id);
    await deleteDoc(docRef);
    callback && callback(true);
  } catch (err) {
    console.log(`Error deleting balance instruction: ${err}`);
    callback && callback(false);
  }
}

export async function getBalanceInstructionsByCriteria(
  formulaId: string,
  accountId: string,
  netExposure: number,
): Promise<BalanceSuggestedInstructions[]> {
  try {
    const collectionRef = collection(DB_LOCATION, BALANCE_SUGGESTED_INSTRUCTIONS_COLLECTION);
    const q = query(
      collectionRef,
      where('formulaId', '==', formulaId),
      where('accountId', '==', accountId),
      where('exposureRange.min', '<=', netExposure),
    );

    const querySnapshot = await getDocs(q);
    const balanceInstructions: BalanceSuggestedInstructions[] = [];
    querySnapshot.forEach((doc) => {
      const balanceInstruction = doc.data() as BalanceSuggestedInstructions;
      // Additional filtering on 'exposureRange.max'
      if (balanceInstruction.exposureRange.max >= netExposure) {
        balanceInstructions.push(balanceInstruction);
      }
    });

    return balanceInstructions;
  } catch (err) {
    console.error(`Error getting balance instructions by criteria: ${err}`);
    return [];
  }
}
